export const TURNSTILE_SCRIPT_ID = 'cf-turnstile-api-script'
export const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
export const TURNSTILE_SCRIPT_LOAD_TIMEOUT_MS = 10_000

export type TurnstileWidgetId = string

export interface TurnstileRenderOptions {
  sitekey: string
  action?: string
  appearance?: 'always' | 'execute' | 'interaction-only'
  callback(token: string): void
  execution?: 'render' | 'execute'
  'error-callback'?(errorCode: string): void
  'expired-callback'?(): void
  'response-field'?: boolean
  'timeout-callback'?(): void
  'unsupported-callback'?(): void
}

export interface TurnstileApi {
  execute(widgetId: TurnstileWidgetId): void
  remove(widgetId: TurnstileWidgetId): void
  render(container: HTMLElement, options: TurnstileRenderOptions): TurnstileWidgetId
  reset(widgetId: TurnstileWidgetId): void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let turnstileScriptPromise: Promise<TurnstileApi> | null = null

export function loadTurnstileScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile can only run in a browser'))
  }

  if (window.turnstile) {
    return Promise.resolve(window.turnstile)
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise
  }

  const { promise, reject, resolve } = Promise.withResolvers<TurnstileApi>()
  const existingScript = document.querySelector<HTMLScriptElement>(`#${TURNSTILE_SCRIPT_ID}`)
  let timeoutId: number | undefined
  let script: HTMLScriptElement

  const handleLoad = () => {
    if (window.turnstile) {
      cleanup()
      resolve(window.turnstile)
      return
    }

    rejectScriptLoad(new Error('Turnstile script loaded without exposing the API'))
  }

  const handleError = () => {
    rejectScriptLoad(new Error('Turnstile script failed to load'))
  }

  const handleTimeout = () => {
    rejectScriptLoad(new Error('Turnstile script timed out'))
  }

  function cleanup() {
    script.removeEventListener('load', handleLoad)
    script.removeEventListener('error', handleError)

    if (timeoutId) {
      window.clearTimeout(timeoutId)
    }
  }

  function rejectScriptLoad(error: Error) {
    cleanup()

    if (!window.turnstile && script.isConnected) {
      script.remove()
    }

    reject(error)
  }

  if (existingScript) {
    script = existingScript
  } else {
    script = document.createElement('script')
    script.id = TURNSTILE_SCRIPT_ID
    script.src = TURNSTILE_SCRIPT_SRC
    script.async = true
    script.defer = true
  }

  script.addEventListener('load', handleLoad, { once: true })
  script.addEventListener('error', handleError, { once: true })
  timeoutId = window.setTimeout(handleTimeout, TURNSTILE_SCRIPT_LOAD_TIMEOUT_MS)

  if (!existingScript) {
    document.head.append(script)
  }

  turnstileScriptPromise = promise.catch(error => {
    turnstileScriptPromise = null
    throw error
  })

  return turnstileScriptPromise
}

export function resetTurnstileScriptForTests() {
  turnstileScriptPromise = null
}
